<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary">mdi-shape-outline</v-icon>
        <span class="px-2">{{ $t('Car Types') }}</span>
        <v-spacer></v-spacer>
        <create-button @create="showCreateDialog"></create-button>
      </v-card-title>
      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="carTypes"
        :search="search"
        :footer-props="footerProps"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            :label="$t('search')"
            class="mx-4"
          ></v-text-field>
        </template>
        <template v-slot:item.is_active="{ item }">
          <v-chip :color="item.is_active ? 'success' : 'error'" dark small>
            {{ item.is_active ? $t('Active') : $t('Inactive') }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mx-2" @click="editType(item)">mdi-pencil</v-icon>
          <v-icon small class="mx-2" @click="deleteType(item, carTypes.indexOf(item))">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ isEdit ? $t('edit_car_type') : $t('add_new_car_type') }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.name_en"
                    :rules="[v => !!v || 'English Name is required']"
                    :label="$t('car_type_name_en') + '*'"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.name_ar"
                    :rules="[v => !!v || 'Arabic Name is required']"
                    :label="$t('car_type_name_ar') + '*'"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.slug"
                    :rules="[v => !!v || 'Slug is required']"
                    :label="$t('slug') + '*'"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.sort_order"
                    type="number"
                    :label="$t('sort_order')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-switch
                    v-model="editedItem.is_active"
                    :label="$t('is_active')"
                  ></v-switch>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialog = false">{{ $t('cancel') }}</v-btn>
            <v-btn color="blue darken-1" text @click="save">{{ $t('Save Changes') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import CreateButton from "@/components/CreateButton";

export default {
  components: { CreateButton },
  data() {
    return {
      carTypes: [],
      isLoading: false,
      search: "",
      dialog: false,
      valid: true,
      isEdit: false,
      editedItem: {
        id: null,
        name_en: "",
        name_ar: "",
        slug: "",
        sort_order: 0,
        is_active: true
      },
      headers: [
        { text: "id", value: "id", align: "start" },
        { text: "car_type_name_en", value: "name_en" },
        { text: "car_type_name_ar", value: "name_ar" },
        { text: "slug", value: "slug" },
        { text: "sort_order", value: "sort_order" },
        { text: "is_active", value: "is_active" },
        { text: "Actions", value: "actions", sortable: false }
      ]
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page")
      };
    }
  },
  mounted() {
    this.loadCarTypes();
  },
  methods: {
    loadCarTypes() {
      this.isLoading = true;
      axios
        .get("/private-car-bookings/types")
        .then(response => {
          this.carTypes = response.data;
        })
        .catch(error => {
          this.$notify({
            title: this.$t("Error"),
            text: "Failed to load car types",
            type: "error"
          });
          console.error(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    showCreateDialog() {
      this.isEdit = false;
      this.editedItem = {
        id: null,
        name_en: "",
        name_ar: "",
        slug: "",
        sort_order: 0,
        is_active: true
      };
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    editType(item) {
      this.isEdit = true;
      this.editedItem = Object.assign({}, item);
      this.editedItem.is_active = !!item.is_active;
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    deleteType(item, index) {
      this.$swal
        .fire({
          title: this.$t("yes_delete"),
          text: "Are you sure you want to delete this car type?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("yes"),
          cancelButtonText: this.$t("cancel")
        })
        .then(result => {
          if (result.isConfirmed) {
            axios
              .delete(`/private-car-bookings/types/${item.id}`)
              .then(() => {
                this.carTypes.splice(index, 1);
                this.$notify({
                  title: this.$t("Success"),
                  text: "Car type deleted successfully",
                  type: "success"
                });
              })
              .catch(error => {
                let msg = error.response?.data?.message || "Failed to delete car type";
                this.$swal(this.$t("Error"), msg, "error");
              });
          }
        });
    },
    save() {
      if (this.$refs.form.validate()) {
        this.isLoading = true;
        this.dialog = false;
        const request = this.isEdit
          ? axios.post(`/private-car-bookings/types/${this.editedItem.id}`, this.editedItem)
          : axios.post("/private-car-bookings/types", this.editedItem);

        request
          .then(() => {
            this.loadCarTypes();
            this.$notify({
              title: this.$t("Success"),
              text: this.isEdit ? "Car type updated" : "Car type created",
              type: "success"
            });
          })
          .catch(error => {
            let msg = error.response?.data?.message || "Save failed";
            this.$swal(this.$t("Error"), msg, "error");
          })
          .then(() => {
            this.isLoading = false;
          });
      }
    }
  }
};
</script>
